import { CrashHandler } from '@enums/crash-handler'
import { CrashStormVerdict } from '@enums/crash-storm-verdict'
import { errorStack } from '@utils/crash-report'

/** How many crashes one page load may send, per error and in total. */
export interface CrashStormLimits {
	readonly perError: number
	readonly perPage: number
}

/** Decides, crash by crash, whether one more crash of the given kind may be sent. */
export type CrashStormGuard = (key: string) => CrashStormVerdict

export const CRASH_STORM_LIMITS: CrashStormLimits = {
	perError: 5,
	perPage: 20,
}

const STACK_FRAMES_IN_KEY = 5
// A frame ends with its line and column, `…/main.js:3:14` or `…/main.js:3:14)`, in every browser's stack format.
const STACK_FRAME = /:\d+:\d+\)?$/
// Never a real key: those always start with the name of who caught the error.
const PAGE_KEY = ''

const stackFrames = (error: unknown): readonly string[] => {
	const stack = errorStack(error)

	if (typeof stack !== 'string') return []

	return stack
		.split('\n')
		.map((line) => line.trim())
		.filter((line) => STACK_FRAME.test(line))
		.slice(0, STACK_FRAMES_IN_KEY)
}

/**
 * What makes two crashes the same error: who caught it, its title and the top frames of its stack. Lines that are
 * not frames (Chrome starts the stack with `Error: <message>`) are left out, so the key is the same in every
 * browser. An error without a stack is keyed on who caught it and its title alone.
 */
export const crashStormKey = (handler: CrashHandler, title: string, error: unknown): string =>
	[handler, title, ...stackFrames(error)].join('\n')

/**
 * Keeps one failing loop from flooding the crash list: each error sends its first crashes up to its limit, the page
 * up to its own limit, so an error whose message changes every time is still bounded. The first crash over each limit
 * is turned into a single warning, so the cut is visible; anything after that is dropped.
 */
export const createCrashStormGuard = (
	limits: CrashStormLimits = CRASH_STORM_LIMITS
): CrashStormGuard => {
	const sentPerKey = new Map<string, number>()
	const warned = new Set<string>()
	const page = { sent: 0 }

	const warnOnce = (warnKey: string, verdict: CrashStormVerdict): CrashStormVerdict => {
		if (warned.has(warnKey)) return CrashStormVerdict.Drop

		warned.add(warnKey)

		return verdict
	}

	return (key: string): CrashStormVerdict => {
		const sentForKey = sentPerKey.get(key) ?? 0

		if (sentForKey >= limits.perError) return warnOnce(key, CrashStormVerdict.ErrorLimitReached)
		if (page.sent >= limits.perPage)
			return warnOnce(PAGE_KEY, CrashStormVerdict.PageLimitReached)

		sentPerKey.set(key, sentForKey + 1)
		page.sent += 1

		return CrashStormVerdict.Send
	}
}
